import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  Eye,
  EyeOff,
  FileArchive,
  Layers,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  fetchLayerSet,
  startLayerDecomposition,
  ImageLayer,
  ImageLayerSet,
} from '../../services/api/imageLayerService';
import { buildPsdBlob, buildZipBlob, downloadBlob } from '../../utils/layerExport';

interface LayerPanelProps {
  imageId: number;
  /** 元画像。分解前のプレビューとして出す */
  imageUrl?: string;
  /** 書き出しファイル名に使う */
  baseFileName?: string;
}

const POLL_INTERVAL_MS = 2500;

const KIND_LABEL: Record<string, string> = {
  background: '背景',
  text: 'テキスト',
  logo: 'ロゴ',
  icon: 'アイコン',
  subject: 'オブジェクト',
  decoration: '装飾',
  object: '要素',
};

const KIND_STYLE: Record<string, string> = {
  background: 'bg-slate-100 text-slate-600',
  text: 'bg-sky-100 text-sky-700',
  logo: 'bg-violet-100 text-violet-700',
  icon: 'bg-emerald-100 text-emerald-700',
  subject: 'bg-amber-100 text-amber-700',
  decoration: 'bg-slate-100 text-slate-600',
  object: 'bg-slate-100 text-slate-600',
};

const LayerPanel: React.FC<LayerPanelProps> = ({ imageId, imageUrl, baseFileName }) => {
  const [layerSet, setLayerSet] = useState<ImageLayerSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState<'psd' | 'zip' | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const set = await fetchLayerSet(imageId);
      setLayerSet(set);
      if (set && (set.status === 'completed' || set.status === 'failed')) {
        stopPolling();
      }
      return set;
    } catch (e) {
      setError('レイヤー情報の取得に失敗しました');
      stopPolling();
      return null;
    }
  }, [imageId, stopPolling]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    return stopPolling;
  }, [load, stopPolling]);

  // 処理中のあいだだけポーリングする
  useEffect(() => {
    const inProgress = layerSet?.status === 'pending' || layerSet?.status === 'processing';
    if (!inProgress || pollRef.current) return;
    pollRef.current = setInterval(load, POLL_INTERVAL_MS);
    return stopPolling;
  }, [layerSet?.status, load, stopPolling]);

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      const set = await startLayerDecomposition(imageId);
      setLayerSet(set);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 503) {
        setError('レイヤー分解エンジンが未設定のため、現在この機能は利用できません');
      } else if (status === 402) {
        setError('クレジット残高が不足しています');
      } else {
        setError(e?.response?.data?.message ?? 'レイヤー分解を開始できませんでした');
      }
    } finally {
      setStarting(false);
    }
  };

  const toggleLayer = (id: number) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExport = async (format: 'psd' | 'zip') => {
    if (!layerSet) return;
    setExporting(format);
    setError(null);
    try {
      const base = baseFileName || `banner_${imageId}`;
      if (format === 'psd') {
        downloadBlob(await buildPsdBlob(layerSet), `${base}_layers.psd`);
      } else {
        downloadBlob(await buildZipBlob(layerSet), `${base}_layers.zip`);
      }
    } catch (e: any) {
      setError(e?.message ?? '書き出しに失敗しました');
    } finally {
      setExporting(null);
    }
  };

  /**
   * サムネイルは bbox の部分だけを拡大して見せる。
   * 1080px のキャンバス全体を 40px に縮めると、小さなテキストが点になって
   * 何のレイヤーか判別できなくなるため。
   */
  const thumbStyle = (layer: ImageLayer): React.CSSProperties => {
    const { x, y, width, height } = layer.bbox;
    if (x == null || y == null || !width || !height) return {};
    return {
      backgroundImage: `url(${layer.url})`,
      backgroundSize: `${100 / width}% ${100 / height}%`,
      backgroundPosition: `${width < 1 ? (x / (1 - width)) * 100 : 0}% ${
        height < 1 ? (y / (1 - height)) * 100 : 0
      }%`,
      backgroundRepeat: 'no-repeat',
    };
  };

  const layers: ImageLayer[] = layerSet?.layers ?? [];
  const visibleLayers = layers.filter((l) => !hiddenIds.has(l.id));
  const isInProgress = layerSet?.status === 'pending' || layerSet?.status === 'processing';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        読み込み中...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 未分解: 説明と開始ボタン */}
      {!layerSet && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
          <Layers className="w-8 h-8 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">レイヤーに分解する</p>
            <p className="mt-1 text-sm text-gray-500">
              Photoshop で開ける PSD に書き出せます。
            </p>
          </div>
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            レイヤーに分解
          </button>
        </div>
      )}

      {/* 処理中 */}
      {isInProgress && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
          <div className="text-sm">
            <p className="font-medium text-gray-900">レイヤーに分解しています</p>
            <p className="text-gray-500">20秒ほどで完了します。このまま少しお待ちください。</p>
          </div>
        </div>
      )}

      {/* 失敗 */}
      {layerSet?.status === 'failed' && (
        <div className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-4">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{layerSet.error_message || 'レイヤー分解に失敗しました'}</span>
          </div>
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="inline-flex items-center gap-2 self-start rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            もう一度試す
          </button>
        </div>
      )}

      {/* 完了: プレビューとレイヤー一覧 */}
      {layerSet?.status === 'completed' && layers.length > 0 && (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {/* 重ね合わせプレビュー。非表示にしたレイヤーは消える */}
            <div
              className="relative w-full self-start overflow-hidden rounded-lg border border-gray-200 bg-[linear-gradient(45deg,#f3f4f6_25%,transparent_25%),linear-gradient(-45deg,#f3f4f6_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#f3f4f6_75%),linear-gradient(-45deg,transparent_75%,#f3f4f6_75%)] bg-[length:16px_16px] bg-[position:0_0,0_8px,8px_-8px,-8px_0]"
              style={{
                aspectRatio: `${layerSet.canvas_width ?? 1} / ${layerSet.canvas_height ?? 1}`,
                maxHeight: '46vh',
              }}
            >
              {visibleLayers.map((layer) => (
                <img
                  key={layer.id}
                  src={layer.url}
                  alt={layer.name}
                  className="absolute inset-0 h-full w-full object-contain"
                  style={{ zIndex: layer.z_index }}
                />
              ))}
            </div>

            <div className="flex min-h-0 flex-col gap-3">
              <div className="text-sm text-gray-500">
                {layers.length} レイヤー / {layerSet.canvas_width}×{layerSet.canvas_height}px
                {layerSet.credits_consumed > 0 && ` / ${layerSet.credits_consumed} クレジット`}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleExport('psd')}
                  disabled={exporting !== null}
                  className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
                >
                  {exporting === 'psd' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  PSD でダウンロード
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('zip')}
                  disabled={exporting !== null}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  {exporting === 'zip' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileArchive className="w-4 h-4" />
                  )}
                  レイヤー PNG（ZIP）
                </button>
              </div>

              <p className="text-xs leading-relaxed text-gray-500">
                PSD の文字はラスタ（画像）レイヤーです。移動・差し替え・非表示はできますが、
                文字ツールでの打ち替えはできません。ZIP には本文をまとめた「テキスト一覧.txt」を同梱しています。
              </p>

              {/* レイヤー一覧。前面から並べる（Photoshop と同じ並び）。
                  表示/非表示を切り替えると左のプレビューが即座に変わるよう、
                  プレビューと同じ画面内に置いてこの列だけでスクロールさせる。 */}
              <ul className="max-h-[34vh] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
            {[...layers].reverse().map((layer) => {
              const hidden = hiddenIds.has(layer.id);
              return (
                <li
                  key={layer.id}
                  className={`flex items-center gap-3 px-3 py-2 ${hidden ? 'opacity-50' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleLayer(layer.id)}
                    className="text-gray-400 transition-colors hover:text-gray-700"
                    aria-label={hidden ? 'レイヤーを表示' : 'レイヤーを非表示'}
                  >
                    {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>

                  {/* 市松模様の上に bbox 部分を拡大表示する（透明部分が分かる） */}
                  <div
                    className="h-14 w-14 shrink-0 rounded border border-gray-200 bg-[linear-gradient(45deg,#e5e7eb_25%,transparent_25%),linear-gradient(-45deg,#e5e7eb_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#e5e7eb_75%),linear-gradient(-45deg,transparent_75%,#e5e7eb_75%)] bg-[length:8px_8px] bg-[position:0_0,0_4px,4px_-4px,-4px_0]"
                  >
                    <div className="h-full w-full rounded" style={thumbStyle(layer)} />
                  </div>

                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      KIND_STYLE[layer.kind] ?? KIND_STYLE.object
                    }`}
                  >
                    {KIND_LABEL[layer.kind] ?? '要素'}
                  </span>

                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900" title={layer.name}>
                    {layer.name}
                  </span>

                  <a
                    href={layer.url}
                    download={`${layer.name}.png`}
                    className="text-gray-400 transition-colors hover:text-gray-700"
                    aria-label="このレイヤーをダウンロード"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </li>
                );
              })}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LayerPanel;
